import { type RefCallback } from 'react';

import {
  graphAreaCell,
  graphAreaContainer,
  graphAreaWrapper,
} from '../SankeyChart.sty.js';
import { SankeyCanvas } from './SankeyCanvas.js';
import { SankeyLabelOverlay } from './SankeyLabelOverlay.js';
import { SankeyOverlay } from './SankeyOverlay.js';
import { useResizeObserver } from '../../../core/hooks/useResizeObserver.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import { OverlayProvider } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { MAX_SIDE_LABEL_WIDTH_RATIO } from '../constants.js';
import { useNodeColors } from '../hooks/use-node-colors.js';
import { useSankeyLayout } from '../hooks/use-sankey-layout.js';
import { SankeyHoverStoreProvider } from '../providers/SankeyHoverStoreProvider.js';
import { SankeyLayoutProvider } from '../providers/SankeyLayoutProvider.js';

interface SankeyGraphAreaContentProps {
  outerRef: RefCallback<HTMLDivElement | null>;
  canvasRef: RefCallback<HTMLDivElement | null>;
  canvasWidth: number;
  height: number;
  maxSideLabelWidth: number | undefined;
  nodeColors: Map<string, string>;
}

function SankeyGraphAreaContent({
  outerRef,
  canvasRef,
  canvasWidth,
  height,
  maxSideLabelWidth,
  nodeColors,
}: Readonly<SankeyGraphAreaContentProps>) {
  const layout = useSankeyLayout();

  return (
    <div ref={outerRef} className={graphAreaContainer}>
      <div className={graphAreaCell}>
        <SankeyLabelOverlay
          zone="left"
          layout={layout}
          maxWidth={maxSideLabelWidth}
        />
      </div>
      <div ref={canvasRef} className={graphAreaCell}>
        <SankeyCanvas
          width={canvasWidth}
          height={height}
          layout={layout}
          nodeColors={nodeColors}
        />
        <SankeyLabelOverlay zone="inside" layout={layout} />
        <SankeyOverlay />
      </div>
      <div className={graphAreaCell}>
        <SankeyLabelOverlay
          zone="right"
          layout={layout}
          maxWidth={maxSideLabelWidth}
        />
      </div>
    </div>
  );
}

SankeyGraphAreaContent['displayName'] = 'SankeyGraphAreaContent';

export function SankeyGraphArea() {
  const { height } = useGraphSize();
  const nodeColors = useNodeColors();

  const { ref: outerRef, width: outerWidth } =
    useResizeObserver<HTMLDivElement>();
  const { ref: canvasRef, width: measuredWidth } =
    useResizeObserver<HTMLDivElement>();
  const canvasWidth = measuredWidth ?? 0;

  const maxSideLabelWidth =
    outerWidth !== undefined && outerWidth > 0
      ? Math.floor(outerWidth * MAX_SIDE_LABEL_WIDTH_RATIO)
      : undefined;

  return (
    <div className={graphAreaWrapper}>
      <SankeyHoverStoreProvider>
        <SankeyLayoutProvider width={canvasWidth} height={height}>
          <OverlayProvider>
            <SankeyGraphAreaContent
              outerRef={outerRef}
              canvasRef={canvasRef}
              canvasWidth={canvasWidth}
              height={height}
              maxSideLabelWidth={maxSideLabelWidth}
              nodeColors={nodeColors}
            />
          </OverlayProvider>
        </SankeyLayoutProvider>
      </SankeyHoverStoreProvider>
    </div>
  );
}

SankeyGraphArea['displayName'] = 'SankeyGraphArea';
